import { useState, useEffect } from 'react';

export default function CourseHero() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollIndicatorVisible, setScrollIndicatorVisible] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

      setScrollProgress(progress);
      setIsScrolled(scrollTop > 50);
      setShowCTA(scrollTop > 600);
      setScrollIndicatorVisible(scrollTop < 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const navigateHome = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/');
    }
  };

  const navLinks = [
    { label: 'Course Structure', id: 'course-structure' },
    { label: 'Module Details', id: 'module-breakdown' },
    { label: 'Learning Outcomes', id: 'learning-outcomes' },
    { label: 'Features', id: 'course-features' },
  ];

  return (
    <section className="relative min-h-[90vh] overflow-hidden">
      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-[3px] bg-black/10">
        <div
          className="h-full bg-gradient-to-r from-[#E48523] via-[#BA3D03] to-[#E8C580] transition-all duration-100 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
        {/* Glowing tip */}
        <div
          className="absolute top-0 h-[3px] w-8 bg-white/60 blur-sm transition-all duration-100 ease-out"
          style={{ left: `calc(${scrollProgress}% - 2rem)`, opacity: scrollProgress > 1 ? 1 : 0 }}
        />
      </div>

      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/a7e2adb44bddae4987c93a18fd620e9c.jpg"
          alt="Course overview background"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0800]/80 via-black/55 to-[#3D1203]/60"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30"></div>
      </div>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          {/* Logo + Back link */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <button onClick={navigateHome} className="flex items-center flex-shrink-0 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/597b2346b2ff492b6c1bd141556b2fdd/301672d98dde029302942d99224febda.png"
                alt="Logo"
                className="h-11 w-auto object-contain"
              />
            </button>
            {/* Back to overview link */}
            <button
              onClick={navigateHome}
              className={`hidden md:inline-flex items-center gap-1.5 text-sm font-medium transition-all whitespace-nowrap cursor-pointer px-3 py-1.5 rounded-lg ${
                isScrolled
                  ? 'text-[#BA3D03] hover:bg-[#FFF9F5]'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <i className="ri-arrow-left-line text-base w-4 h-4 flex items-center justify-center"></i>
              Back to overview
            </button>
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-[#3D1203] hover:text-[#BA3D03] hover:bg-[#FFF9F5]'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => scrollTo('course-cta')}
              className={`relative px-6 py-2.5 bg-[#BA3D03] text-white rounded-lg text-sm font-bold hover:bg-[#3D1203] transition-all whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg group ${
                showCTA ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'
              }`}
              style={{ transition: 'opacity 0.4s, transform 0.4s, background 0.2s, box-shadow 0.2s' }}
            >
              <span className="relative flex items-center gap-2">
                <i className="ri-price-tag-3-line text-base w-4 h-4 flex items-center justify-center"></i>
                Request Company Pricing
              </span>
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              isScrolled ? 'text-[#0E0503] hover:bg-[#FFF9F5]' : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#E8C580] shadow-lg">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
              <button
                onClick={navigateHome}
                className="text-left px-4 py-3 rounded-lg text-sm font-medium text-[#BA3D03] hover:bg-[#FFF9F5] transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-arrow-left-line"></i>
                Back to overview
              </button>
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="text-left px-4 py-3 rounded-lg text-sm font-medium text-[#3D1203] hover:text-[#BA3D03] hover:bg-[#FFF9F5] transition-all whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => scrollTo('course-cta')}
                className="mt-2 px-4 py-3 bg-[#BA3D03] text-white rounded-lg text-sm font-bold hover:bg-[#3D1203] transition-all whitespace-nowrap cursor-pointer text-center flex items-center justify-center gap-2"
              >
                <i className="ri-price-tag-3-line"></i>
                Request Company Pricing
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 w-full min-h-[90vh] flex items-center">
        <div className="max-w-7xl mx-auto px-5 lg:px-12 pt-24 sm:pt-36 pb-16 sm:pb-28 w-full">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-[#E48523]/90 backdrop-blur-sm text-white rounded-full text-xs sm:text-sm font-semibold mb-6 sm:mb-10 shadow-lg border border-white/10">
              <i className="ri-book-open-line mr-2"></i>
              Course Overview
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-5 sm:mb-8 drop-shadow-lg">
              Country-centred Practice for NSW Built Environment Teams
            </h1>

            <p className="text-base sm:text-xl lg:text-2xl text-white/90 mb-8 sm:mb-14 max-w-3xl leading-relaxed drop-shadow-sm">
              A self-paced course for NSW built environment teams to connect with Country, engage respectfully with Traditional Custodians, protect ICIP, and bring genuine cultural understanding into every stage of planning, design and delivery.
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-6 mb-8 sm:mb-12">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-4 py-3 sm:px-5 sm:py-4">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#E48523]/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-lg sm:text-xl text-[#E8C580]"></i>
                </div>
                <div>
                  <div className="text-lg sm:text-xl font-bold text-white">12 Months</div>
                  <div className="text-xs text-white/60 uppercase tracking-wide">Access Period</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-4 py-3 sm:px-5 sm:py-4">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#E48523]/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-book-2-line text-lg sm:text-xl text-[#E8C580]"></i>
                </div>
                <div>
                  <div className="text-lg sm:text-xl font-bold text-white">4 Modules</div>
                  <div className="text-xs text-white/60 uppercase tracking-wide">Structured Pathway</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-4 py-3 sm:px-5 sm:py-4">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#E48523]/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-lg sm:text-xl text-[#E8C580]"></i>
                </div>
                <div>
                  <div className="text-lg sm:text-xl font-bold text-white">Self-Paced</div>
                  <div className="text-xs text-white/60 uppercase tracking-wide">Learn Anytime</div>
                </div>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-white/50 italic mb-8 sm:mb-10 flex items-start sm:items-center gap-2">
              <i className="ri-leaf-line text-[#E48523]/70 flex-shrink-0 mt-0.5 sm:mt-0"></i>
              Content developed in direct consultation with Traditional Custodians — shaped by lived experience and genuine cultural authority, not policy alone.
            </p>

            <button
              onClick={() => scrollTo('module-breakdown')}
              className="w-full sm:w-auto px-8 py-4 bg-[#BA3D03] text-white rounded-lg text-base font-semibold hover:bg-[#3D1203] transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer inline-flex items-center justify-center sm:justify-start gap-2 border border-white/10"
            >
              View module breakdown
              <i className="ri-arrow-down-line text-lg w-5 h-5 flex items-center justify-center"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Scroll Down Indicator */}
      <div
        className="absolute bottom-8 left-0 right-0 z-20 flex flex-col items-center gap-2 pointer-events-none transition-all duration-500"
        style={{
          opacity: scrollIndicatorVisible ? 1 : 0,
          transform: scrollIndicatorVisible ? 'translateY(0)' : 'translateY(12px)',
        }}
      >
        <span className="text-white/50 text-xs uppercase tracking-widest font-medium">Scroll</span>
        <div className="flex flex-col items-center gap-1">
          <i
            className="ri-arrow-down-s-line text-white/60 text-2xl"
            style={{
              animation: 'scrollBounce 1.6s ease-in-out infinite',
            }}
          ></i>
          <i
            className="ri-arrow-down-s-line text-white/30 text-2xl -mt-3"
            style={{
              animation: 'scrollBounce 1.6s ease-in-out infinite 0.2s',
            }}
          ></i>
        </div>
      </div>

      <style>{`
        @keyframes scrollBounce {
          0%, 100% { transform: translateY(0); opacity: 0.6; }
          50% { transform: translateY(6px); opacity: 1; }
        }
      `}</style>
    </section>
  );
}
